import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, Monitor, Users, FileText, LayoutDashboard,
  BookOpen, GraduationCap, ClipboardList, Shield,
  BarChart3, Globe, Printer, UserCheck, ShoppingBag, MessageSquare,
  Mail, ArrowRight, CreditCard, Clock, Plus
} from "lucide-react";
import { Separator } from "../components/ui/separator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const systemFeatures = [
  { icon: Users, title: "Student Enrollment System", desc: "Complete online enrollment — parent info, student details, payment, and instant document generation. Families are set up the same day." },
  { icon: LayoutDashboard, title: "Admin Dashboard", desc: "Full admin panel to manage enrollments, view stats, search families, filter by status, export data, and manage your entire school." },
  { icon: UserCheck, title: "Parent Portal", desc: "Families get their own secure login to view documents, manage students, re-enroll, and access school resources." },
  { icon: FileText, title: "Automatic PDF Documents", desc: "Enrollment forms, welcome letters, and BOE instructions auto-generated and emailed on enrollment. Re-downloadable anytime." },
  { icon: Shield, title: "Security & Backups", desc: "Enterprise-grade hosting with SSL, daily backups, and guaranteed uptime so your school data is always safe." },
  { icon: Mail, title: "Automated Emails", desc: "Enrollment confirmations, welcome letters, reminders, and notifications — all sent automatically to families." },
  { icon: Shield, title: "Your Own Independent School", desc: "Your school is its own independent operation. We build the technology — you run the school your way." },
  { icon: Globe, title: "Free Domain Name", desc: "Your school gets its own custom domain name, included free for the first year ($25/yr renewal after that)." },
  { icon: Globe, title: "Serve Alabama Families", desc: "Built for Alabama church schools with administrative tools designed for Alabama church schools." },
];

const curriculumFeatures = [
  { icon: BookOpen, title: "Full K-12 Curriculum", desc: "Complete core subjects for kindergarten through 12th grade, ready to use." },
  { icon: Monitor, title: "AI-Powered Lesson Plans", desc: "Structured lesson plans generated for every subject and grade level." },
  { icon: FileText, title: "Worksheets & Activities", desc: "Printable worksheets, activities, and learning materials for every lesson." },
  { icon: GraduationCap, title: "Assessments & Grading", desc: "Built-in assessments, quizzes, and grading tools to track student progress." },
  { icon: BarChart3, title: "Progress Tracking", desc: "Visual dashboards showing student progress across all subjects and grade levels." },
  { icon: Globe, title: "Serve Families Nationwide", desc: "With curriculum included, your school can enroll and serve families from any state in the country." },
];

const included = [
  "Custom church school website",
  "Online enrollment system",
  "Parent portal with secure login",
  "Admin dashboard",
  "Automatic PDF document generation",
  "Automated email notifications",
  "Free domain name (first year)",
  "Ongoing technical support",
  "System updates & maintenance",
];

export default function StartSchoolPage() {
  return (
    <div data-testid="start-school-page">
      {/* Header */}
      <section className="bg-[#1C2E4A] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Get Started</p>
          <h1 data-testid="start-school-headline" className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl">
            Start Your Own Church School
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/70 max-w-2xl leading-relaxed">
            We build your complete church school system — enrollment, parent portal, admin dashboard, documents, store, and more. You run the school. We handle the technology.
          </p>
          <Link to="/get-started" className="mt-8 inline-flex items-center gap-2 bg-[#C8A97E] text-white hover:bg-[#B3956A] px-8 py-3 rounded-md transition-colors font-medium">
            Get Started Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* System Features */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Your Church School System</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">Everything That's Included</h2>
            <p className="text-sm text-[#595959] mt-3 max-w-xl mx-auto">A complete, custom-built system for your church school — ready to enroll families on day one.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {systemFeatures.map((f, i) => (
              <div key={f.title} data-testid={`system-feature-${i}`} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="w-10 h-10 rounded-lg bg-[#1C2E4A]/5 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-[#1C2E4A]" />
                </div>
                <h3 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">{f.title}</h3>
                <p className="text-xs text-[#595959] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Add-On */}
      <section className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Optional Add-On</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">K-12 Curriculum Package</h2>
            <p className="text-sm text-[#595959] mt-3 max-w-xl mx-auto">Add a complete K-12 curriculum to your church school system. One-time fee of $1,499. Best value when added during setup.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {curriculumFeatures.map((f, i) => (
              <div key={f.title} data-testid={`curriculum-feature-${i}`} className="bg-white rounded-xl border border-[#E5E2DC] p-6 hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="w-10 h-10 rounded-lg bg-[#C8A97E]/10 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-[#C8A97E]" />
                </div>
                <h3 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">{f.title}</h3>
                <p className="text-xs text-[#595959] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-[#595959]/70 mt-6">Need more than basic K-12? Custom curriculum packages are available — contact us for a quote.</p>
        </div>
      </section>

      {/* Available Add-ons */}
      <AddonsPreviewSection />

      {/* Pricing */}
      <section data-testid="pricing-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Pricing</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">Simple, Transparent Pricing</h2>
          </div>

          <div className="max-w-xl mx-auto">
            <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] shadow-md overflow-hidden">
              <div className="bg-[#1C2E4A] px-8 py-10 text-center">
                <h3 className="font-serif text-2xl font-semibold text-white mb-6">Church School System</h3>
                <div className="space-y-3">
                  <div>
                    <span data-testid="price-setup" className="font-serif text-4xl font-bold text-white">$999</span>
                    <p className="text-xs text-white/50 mt-1">One-time setup fee</p>
                  </div>
                  <div className="text-white/30 text-lg">+</div>
                  <div>
                    <span data-testid="price-monthly" className="font-serif text-4xl font-bold text-white">$199</span>
                    <span className="text-white/50 text-lg">/mo</span>
                    <p className="text-xs text-white/50 mt-1">Monthly service (ongoing)</p>
                  </div>
                </div>
              </div>

              <div className="px-8 py-5 bg-white border-b border-[#E5E2DC]">
                <h4 className="font-medium text-[#1C2E4A] mb-2 text-sm">How It Works</h4>
                <ul className="space-y-1.5 text-sm text-[#595959]">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#1C2E4A] shrink-0">1.</span>
                    Fill out our intake form with your school details
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#1C2E4A] shrink-0">2.</span>
                    Pay the $999 setup fee + first month ($199) to get started
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-[#1C2E4A] shrink-0">3.</span>
                    We build your complete school system and launch it for you
                  </li>
                </ul>
              </div>

              <div className="px-8 py-6">
                <h4 className="font-medium text-[#1C2E4A] mb-3 text-sm uppercase tracking-wider">What's Included</h4>
                <ul className="space-y-2 mb-6">
                  {included.map((item, i) => (
                    <li key={i} data-testid={`included-item-${i}`} className="flex items-center gap-2.5 text-sm text-[#595959]">
                      <CheckCircle2 className="w-4 h-4 text-[#8F9779] shrink-0" /> {item}
                    </li>
                  ))}
                </ul>

                <Separator className="my-6 bg-[#E5E2DC]" />

                <div className="bg-[#C8A97E]/5 rounded-lg border border-[#C8A97E]/20 p-4 mb-6">
                  <p className="text-sm font-medium text-[#1C2E4A]">Optional: K-12 Curriculum — +$1,499 (one-time)</p>
                  <p className="text-xs text-[#595959] mt-1">Add curriculum during setup and get your first month free ($199 savings). Adding it later will cost more as a separate project.</p>
                </div>

                <Link
                  to="/get-started"
                  data-testid="get-started-btn"
                  className="w-full bg-[#C8A97E] text-white hover:bg-[#B3956A] px-6 py-3.5 rounded-md transition-colors font-medium flex items-center justify-center gap-2 text-base"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <p className="text-xs text-[#595959]/60 text-center mt-3">Domain included free first year. $25 renewal at month 12.</p>
              </div>
            </div>

            {/* Already set up */}
            <div className="mt-6 bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] shadow-sm p-6">
              <p className="text-sm text-[#595959] text-center mb-4">
                <span className="font-medium text-[#1C2E4A]">Already set up?</span> Pay your monthly service fee here.
              </p>
              <MonthlyPayButton />
            </div>

            <p className="text-center text-xs text-[#595959]/60 mt-6">Secure payments processed through Stripe.</p>
          </div>
        </div>
      </section>

      {/* Project Timeline & Review Policy */}
      <section data-testid="timeline-section" className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">What to Expect</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">Project Timeline &amp; Post-Launch Review</h2>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-xl border border-[#E5E2DC] p-8 mb-8">
            <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#C8A97E]" />
              Estimated Timeline
            </h3>
            <p data-testid="timeline-estimate" className="text-sm text-[#595959] leading-relaxed mb-4">
              The estimated turnaround time for the Basic Church School Setup Package is <span className="font-semibold text-[#1C2E4A]">3 to 4 weeks</span> after all required information, documents, branding materials, and payment have been received.
            </p>
            <p className="text-sm text-[#595959] leading-relaxed mb-4">
              The project timeline does not begin until the customer has submitted a completed intake form and provided all requested materials. Delays caused by missing information, late responses, requested approvals, or circumstances beyond our control may extend the estimated completion date.
            </p>
            <p className="text-sm text-[#595959] leading-relaxed">
              Rush completion is not included with the Basic Church School Setup Package and, when available, may be offered for an additional fee.
            </p>
          </div>

          {/* Review Period */}
          <div className="bg-white rounded-xl border border-[#E5E2DC] p-8">
            <h3 className="font-serif text-lg font-semibold text-[#1C2E4A] mb-4 flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#C8A97E]" />
              7-Day Post-Launch Review
            </h3>
            <p data-testid="review-period" className="text-sm text-[#595959] leading-relaxed mb-4">
              Upon completion of your church school website, you will have <span className="font-semibold text-[#1C2E4A]">7 calendar days</span> to thoroughly review the website and request minor adjustments at no additional charge.
            </p>
            <div className="mb-4">
              <p className="text-sm font-medium text-[#1C2E4A] mb-2">Minor adjustments include:</p>
              <ul className="space-y-1.5">
                {["Correcting text", "Replacing images", "Updating contact information", "Adjusting colors", "Fixing formatting", "Correcting links", "Other small cosmetic changes"].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#595959]">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#8F9779] shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#1C2E4A]/5 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-[#1C2E4A] mb-1.5">Not included in the review period:</p>
              <p className="text-xs text-[#595959] leading-relaxed">
                Redesigns, additional pages, new features, changes to the approved layout, changes to functionality, or requests that substantially alter the original scope. Such requests will require a separate quote and may incur additional fees.
              </p>
            </div>
            <p className="text-xs text-[#595959]/70 leading-relaxed">
              All revision requests must be submitted within 7 calendar days of project completion. After the review period has ended, the project will be considered complete, and any additional changes or updates will be billed at our current rates.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function MonthlyPayButton() {
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/checkout/stripe`, { package: "monthly", origin_url: origin, payment_method: "stripe" });
      if (res.data.url) window.location.href = res.data.url;
    } catch { }
    finally { setLoading(false); }
  };

  return (
    <button onClick={handlePay} disabled={loading}
      data-testid="pay-monthly-stripe-btn"
      className="w-full bg-[#1C2E4A] text-white hover:bg-[#152238] px-6 py-2.5 rounded-md transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
      <CreditCard className="w-4 h-4" />
      {loading ? "Processing..." : "Pay $199/mo"}
    </button>
  );
}


function AddonsPreviewSection() {
  const [addons, setAddons] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/addons`;
    fetch(url)
      .then(r => r.json())
      .then(data => { setAddons(data.filter(a => a.active)); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  if (!loaded || addons.length === 0) return null;

  return (
    <section data-testid="addons-preview-section" className="py-20 md:py-28 bg-white border-t border-[#E5E2DC]">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Optional Add-ons</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">Enhance Your School</h2>
          <p className="mt-3 text-sm text-[#595959] max-w-xl mx-auto">Add any of these features to your church school system. All add-ons are one-time fees — you'll select them after completing your intake form.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addons.map(addon => {
            const onSale = addon.sale_price && addon.sale_price > 0 && addon.sale_price < addon.price;
            return (
              <div key={addon.id} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-5 flex items-start justify-between hover:shadow-sm transition-shadow">
                <div className="flex-1 mr-4">
                  <p className="text-sm font-semibold text-[#1C2E4A]">{addon.name}</p>
                  <p className="text-xs text-[#595959] mt-1 leading-relaxed">{addon.description}</p>
                </div>
                <div className="shrink-0 text-right">
                  {onSale ? (
                    <>
                      <p className="text-sm text-[#595959] line-through">+${addon.price.toLocaleString()}</p>
                      <p className="font-serif font-bold text-[#C8A97E] text-lg">+${addon.sale_price.toLocaleString()}</p>
                    </>
                  ) : (
                    <p className="font-serif font-bold text-[#C8A97E] text-lg">+${addon.price.toLocaleString()}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-[#595959]/60 mt-8">Add-ons are optional and can be added during checkout or requested later.</p>
      </div>
    </section>
  );
}
